import { Grid, Paper, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { OrderedOrderHeaderViewModel } from '../models/orderedModel';
import CustomAvatar from 'components/ui/customAvatar';
import { MissingPhoto } from 'Interfaces/IGlobalTypeConfig';
import { useTranslation } from 'react-i18next';

interface OrderedLeftSideItemProps {
  order: OrderedOrderHeaderViewModel;
  selectedOrderId: string | null;
  onSelectOrder: (order: OrderedOrderHeaderViewModel) => void;
}

const OrderedLeftSideItem: React.FC<OrderedLeftSideItemProps> = ({
  order,
  selectedOrderId,
  onSelectOrder,
}) => {
  const { t } = useTranslation();

  // Bruk nye linjer
  const lines = order.lines ?? [];

  // Total = sum(orderedQuantity * unitPrice) for alle purchase-lines
  const totalCost = lines.reduce((total: number, line) => {
    const ordered = Number(line.orderedQuantity ?? 0);
    const unit = Number(line.unitPrice ?? 0);
    return total + ordered * unit;
  }, 0);

  return (
    <Paper
      variant='elevation3'
      elevation={3}
      sx={{
        p: 1,
        mb: 1,
        borderRadius: 3,
        backgroundColor:
          order.orderId === selectedOrderId ? 'primary.light' : 'primary.main',
        cursor: 'pointer',
      }}
      onClick={() => onSelectOrder(order)}
    >
      <Grid
        container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Grid
          item
          xs={12}
          sm={3}
          sx={{
            width: 160,
            height: 88,
            borderTopLeftRadius: 10,
            borderBottomLeftRadius: 10,
            backgroundColor: 'primary.text',
            display: 'flex',
          }}
        >
          {order.vendor?.url ? (
            <Tooltip title={order.vendor?.name}>
              <img
                src={order.vendor?.url || MissingPhoto.COMPANY_URL}
                style={{
                  padding: 0,
                  width: 160,
                  height: 88,
                  borderRadius: 10,
                  objectFit: 'scale-down',
                }}
                alt='previewUrl'
              />
            </Tooltip>
          ) : (
            <CustomAvatar
              avatarName={order.vendor?.name || ''}
              imgWidth={160}
              imgHeight={88}
            />
          )}
        </Grid>

        <Grid
          item
          xs={12}
          sm={9}
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Grid item xs={7} sx={{ pl: 2 }}>
            <Typography sx={{ fontWeight: 'bold' }}>
              {order.orderNo ?? 'No Order No'}
            </Typography>

            <Typography sx={{ fontSize: '0.9rem' }}>
              {t('order.ordered.vendorOrderNumber', {
                vendorOrderNumber:
                  order?.vendorOrderNumber ??
                  t('order.ordered.missing', 'missing'),
                defaultValue: 'Vendor order number: {{vendorOrderNumber}}',
              })}
            </Typography>

            <Typography sx={{ fontSize: '0.9rem' }}>
              {t('order.ordered.trackingNo', {
                trackingNo:
                  order?.trackingNo ?? t('order.ordered.missing', 'missing'),
                defaultValue: 'Tracking number: {{trackingNo}}',
              })}
            </Typography>
          </Grid>

          <Grid
            item
            xs={2}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            <Typography sx={{ fontSize: '0.9rem' }}>{' ... '}</Typography>
            <Typography sx={{ fontWeight: 'bold' }}>
              {t('order.orderPost.totalCost', 'totalCost')}
            </Typography>
            <Typography sx={{ fontWeight: 'bold' }}>
              {t('order.orderPost.freightCost', 'freightCost')}
            </Typography>
          </Grid>

          <Grid
            item
            xs={3}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            <Typography sx={{ fontSize: '0.9rem' }}>
              {lines.length} {t('order.ordered.items', 'items')}
            </Typography>

            <Typography sx={{ fontWeight: 'bold' }}>
              {totalCost.toFixed(2)} NOK
            </Typography>

            <Typography sx={{ fontWeight: 'bold' }}>
              {Number(order.shippingCost ?? 0).toFixed(2)} NOK
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default OrderedLeftSideItem;
